import { useCallback, useEffect, useRef, useState } from "react";
import { CAMPO, CampoDeBusca, Recado } from "./ui";
import Carregando from "./Carregando";
import {
  editarSugestao,
  excluirSugestao,
  lerHistoricoDaSugestao,
  lerSugestoes,
  mensagemDaSugestao,
  mudarStatusDaSugestao,
  registrarSugestao,
  salvarCategoriaDeSugestao,
  ROTULO_DA_ORIGEM,
  ROTULO_DA_PRIORIDADE,
  ROTULO_DO_STATUS,
  type CategoriaDeSugestao,
  type ComentarioDoClickUp,
  type EventoDaSugestao,
  type ListaDeSugestoes,
  type Origem,
  type Prioridade,
  type ProcedenciaDoClickUp,
  type Resposta,
  type StatusDaSugestao,
  type Sugestao,
  type SugestaoExcluida,
} from "../lib/sugestoes";
import { useRolarAoEscolher } from "../lib/rolarPara";

// Admin › Sugestões — o que os alunos pedem, registrado pelo CS e tratado por
// Produtos (000301, G9 do Plano de Melhorias de Produtos; Victor decidiu em
// 24/09 que as sugestões moram NO HUB).
//
// ---------------------------------------------------------------------------
// Os três papéis numa tela só
// ---------------------------------------------------------------------------
// O CS REGISTRA (o formulário do alto) e ACOMPANHA o retorno; Produtos TRATA
// (o status e o retorno, em cada linha) e mantém as CATEGORIAS (o bloco do
// fim). Quem pode o quê é o BANCO: a aba Sugestões dá registrar e tratar, e as
// categorias pedem editar no Catálogo. A tela não esconde botão por papel — a
// recusa vem com a frase que diz onde se pede, a mesma regra do Catálogo.
//
// ---------------------------------------------------------------------------
// O espelho na Central SAIU
// ---------------------------------------------------------------------------
// Até 30/09 a sugestão de categoria "que pede professor" ia também para a
// Central de Professores, pela porta única. A aba de lá saiu, o espelho foi
// desligado (000331) e o código dele foi retirado (000333): as sugestões moram
// só aqui, e a linha não tem mais chip de envio.
//
// ---------------------------------------------------------------------------
// As ANTIGAS do ClickUp (000319), e a EXCLUSÃO que só o master faz
// ---------------------------------------------------------------------------
// Em 28/09 Victor mandou migrar a lista "Sugestões dos Alunos" do ClickUp e
// deixá-la aqui como NOVAS. A linha da importada diz de onde veio (o link do
// ClickUp, que o banco deriva), e o histórico dela traz o que só o ClickUp
// sabia — o status de lá e a conversa da triagem antiga.
//
// No mesmo dia ele pediu a exclusão, "somente os masters". O botão só aparece
// para o master (`podeExcluir` vem de `minhas_permissoes`, como o `podeEditar`
// do Catálogo): para qualquer outro ele seria recusado sempre, e botão que
// recusa sempre ensina a ignorar botões. Quem recusa continua sendo o banco.
//
// ---------------------------------------------------------------------------
// UM ato aberto por vez
// ---------------------------------------------------------------------------
// O formulário de registro, o tratamento de uma linha e a edição de outra
// moram num estado só. Dois abertos convidam a confirmar o errado, e para quem
// prova, dois `data-campo="retorno"` no DOM fariam um seletor pegar o primeiro
// — a cicatriz dos sete rótulos iguais da calculadora.

type Ato =
  | {
      tipo: "registrar";
      titulo: string;
      descricao: string;
      curso: string;
      categoria: string;
      prioridade: Prioridade;
      origem: Origem;
    }
  | { tipo: "tratar"; id: string; status: StatusDaSugestao; retorno: string; motivo: string }
  | {
      tipo: "editar";
      id: string;
      titulo: string;
      descricao: string;
      curso: string;
      categoria: string;
      prioridade: Prioridade;
      origem: Origem;
      motivo: string;
    }
  | { tipo: "excluir"; id: string; motivo: string };

const STATUS: StatusDaSugestao[] = ["nova", "em_andamento", "atendida", "nao_atendida"];
const PRIORIDADES: Prioridade[] = ["alta", "media", "baixa"];
// O Fórum entrou em 28/09 (000321), a pedido de Victor.
const ORIGENS: Origem[] = ["whatsapp", "email", "area_aluno", "forum", "formulario", "pesquisa", "suporte", "outro"];

/** A cor do status — família de ATENÇÃO para o que espera, de CONFIRMAÇÃO para o atendido. */
const CLASSE_DO_STATUS: Record<StatusDaSugestao, string> = {
  nova: "bg-alerta-fundo text-alerta-tinta",
  em_andamento: "bg-chip text-chip-tinta",
  atendida: "bg-aviso-fundo text-aviso-tinta",
  nao_atendida: "bg-erro-fundo text-erro-tinta",
};

/**
 * A PRIORIDADE ao lado do status (pedido de Victor, 28/09). Contorno, e não
 * fundo cheio, para não se confundir com o status: "Não atendida" e "alta"
 * seriam dois vermelhos iguais lado a lado. A alta leva o traço duplo e o peso;
 * a média e a baixa ficam no tom de apoio. O texto diz "Prioridade" por
 * extenso: sem ele, "Alta" ao lado de "Nova" se leria como um segundo status.
 */
const CLASSE_DA_PRIORIDADE: Record<Prioridade, string> = {
  alta: "border-2 border-erro-borda font-bold text-erro-tinta",
  media: "border border-borda font-semibold text-tinta",
  baixa: "border border-borda text-cinza",
};

function dataCurta(iso: string): string {
  return new Date(iso).toLocaleDateString("pt-BR");
}

function dataEHora(iso: string): string {
  return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

/** A palavra no número certo — "1 print", "2 prints" (a cicatriz do "cerca de 1 páginas"). */
function plural(n: number, um: string, varios: string): string {
  return `${n} ${n === 1 ? um : varios}`;
}

export default function AdminSugestoes({ podeExcluir = false }: { podeExcluir?: boolean }) {
  const [lista, setLista] = useState<ListaDeSugestoes | null>(null);
  const [erroDeLeitura, setErroDeLeitura] = useState<string | null>(null);
  const [status, setStatus] = useState<StatusDaSugestao | "">("");
  const [curso, setCurso] = useState("");
  const [categoria, setCategoria] = useState("");
  const [prioridade, setPrioridade] = useState<Prioridade | "">("");
  const [origem, setOrigem] = useState<Origem | "">("");
  const [busca, setBusca] = useState("");
  const [buscaAplicada, setBuscaAplicada] = useState("");
  const [limite, setLimite] = useState(50);
  const [ato, setAto] = useState<Ato | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [recado, setRecado] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);
  // A falha carrega ONDE aconteceu: o erro de um ato fica perto do botão dele.
  const [falha, setFalha] = useState<{ onde: string; texto: string } | null>(null);
  const caixaDoRegistro = useRef<HTMLElement | null>(null);
  const ultimoPedido = useRef(0);

  const recarregar = useCallback(async () => {
    const meu = ++ultimoPedido.current;
    const r = await lerSugestoes({
      status: status || null,
      curso: curso || null,
      categoria: categoria || null,
      prioridade: prioridade || null,
      origem: origem || null,
      busca: buscaAplicada || null,
      limite,
    });
    // Só vale a resposta do ÚLTIMO pedido. Com quatro filtros (000321), trocar
    // dois em seguida faz a resposta do primeiro poder chegar depois da do
    // segundo — e a lista desenharia um recorte que já não é o dos combos.
    if (meu !== ultimoPedido.current) return;
    if (!r.ok) {
      setErroDeLeitura(mensagemDaSugestao(r as Resposta));
      return;
    }
    setErroDeLeitura(null);
    setLista(r as ListaDeSugestoes);
  }, [status, curso, categoria, prioridade, origem, buscaAplicada, limite]);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  const registrando = ato?.tipo === "registrar" ? ato : null;
  useRolarAoEscolher(caixaDoRegistro, registrando ? "registrar" : null);

  async function registrar() {
    if (!registrando) return;
    setSalvando(true);
    setFalha(null);
    setRecado(null);
    const r = await registrarSugestao(registrando);
    setSalvando(false);
    if (!r.ok) {
      setFalha({ onde: "registrar", texto: mensagemDaSugestao(r) });
      return;
    }
    setAto(null);
    setRecado({ tipo: "ok", texto: mensagemDaSugestao(r) });
    await recarregar();
  }

  async function tratar() {
    if (ato?.tipo !== "tratar") return;
    setSalvando(true);
    setFalha(null);
    setRecado(null);
    const r = await mudarStatusDaSugestao(ato.id, ato.status, ato.retorno, ato.motivo);
    setSalvando(false);
    if (!r.ok) {
      // A recusa NÃO fecha o formulário: ela diz o que falta (o retorno, o
      // motivo), e fechar jogaria fora o que a pessoa escreveu.
      setFalha({ onde: `tratar:${ato.id}`, texto: mensagemDaSugestao(r) });
      return;
    }
    setAto(null);
    setRecado({ tipo: "ok", texto: mensagemDaSugestao(r) });
    await recarregar();
  }

  async function salvarEdicao() {
    if (ato?.tipo !== "editar") return;
    setSalvando(true);
    setFalha(null);
    setRecado(null);
    const r = await editarSugestao(ato.id, {
      titulo: ato.titulo,
      descricao: ato.descricao,
      curso: ato.curso,
      categoria: ato.categoria,
      prioridade: ato.prioridade,
      origem: ato.origem,
      motivo: ato.motivo,
    });
    setSalvando(false);
    if (!r.ok) {
      setFalha({ onde: `editar:${ato.id}`, texto: mensagemDaSugestao(r) });
      return;
    }
    setAto(null);
    setRecado({ tipo: "ok", texto: mensagemDaSugestao(r) });
    await recarregar();
  }

  async function excluir() {
    if (ato?.tipo !== "excluir") return;
    setSalvando(true);
    setFalha(null);
    setRecado(null);
    const r = await excluirSugestao(ato.id, ato.motivo);
    setSalvando(false);
    if (!r.ok) {
      // A recusa não fecha a confirmação: o motivo escrito continua lá.
      setFalha({ onde: `excluir:${ato.id}`, texto: mensagemDaSugestao(r) });
      return;
    }
    setAto(null);
    setRecado({ tipo: "ok", texto: mensagemDaSugestao(r) });
    await recarregar();
  }

  if (erroDeLeitura && !lista) {
    return (
      <div className="surface rounded-cf-lg p-6" data-sugestoes-erro>
        <h2 className="font-display text-lg font-bold text-titulo">As sugestões não vieram</h2>
        <p className="mt-2 text-sm text-cinza">{erroDeLeitura}</p>
      </div>
    );
  }
  if (!lista) return <Carregando />;

  const categoriasAtivas = lista.categorias.filter((c) => c.ativo);
  const totalGeral = STATUS.reduce((s, k) => s + (lista.contagem[k] ?? 0), 0);

  return (
    <div className="flex flex-col gap-5" data-aba-sugestoes>
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-lg font-bold text-titulo">Sugestões dos alunos</h2>
          <p className="mt-1 max-w-3xl text-sm text-cinza">
            O CS registra o que os alunos pedem; Produtos trata e responde. As sugestões moram só
            aqui: desde 30/09 elas não vão mais para a Central de Professores.
          </p>
        </div>
        <button
          type="button"
          data-abrir-registro
          onClick={() => {
            setFalha(null);
            setRecado(null);
            setAto({
              tipo: "registrar",
              titulo: "",
              descricao: "",
              curso: "",
              categoria: categoriasAtivas[0]?.id ?? "",
              prioridade: "media",
              origem: "whatsapp",
            });
          }}
          className="pill-cta min-h-11 px-5 text-sm"
        >
          Registrar sugestão
        </button>
      </header>

      {/* ── Registrar ──────────────────────────────────────────────────────── */}
      {registrando && (
        <section
          ref={caixaDoRegistro}
          role="group"
          aria-labelledby="titulo-registrar-sugestao"
          className="surface rise p-6"
          data-form-sugestao
        >
          <h3 id="titulo-registrar-sugestao" className="font-display text-lg font-bold text-titulo">
            Registrar sugestão
          </h3>
          {/* Dito ANTES do clique: o texto vem de uma conversa, e a lista é lida
              pela equipe inteira. */}
          <p className="mt-1 text-sm text-cinza">
            Sem dado pessoal do aluno: nome, e-mail e telefone não entram aqui. Registre o que foi
            pedido.
          </p>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5 sm:col-span-2">
              <span className="text-sm font-semibold text-tinta">Título</span>
              <input
                data-campo="titulo-sugestao"
                className={CAMPO}
                maxLength={200}
                value={registrando.titulo}
                onChange={(e) => setAto({ ...registrando, titulo: e.target.value })}
                placeholder="Ex.: mais exemplos práticos de CBS na aula 3"
              />
            </label>
            <label className="flex flex-col gap-1.5 sm:col-span-2">
              <span className="text-sm font-semibold text-tinta">Descrição</span>
              <textarea
                data-campo="descricao-sugestao"
                rows={3}
                className={`${CAMPO} min-h-20 resize-y py-2`}
                value={registrando.descricao}
                onChange={(e) => setAto({ ...registrando, descricao: e.target.value })}
                placeholder="O que os alunos pediram, com as palavras deles quando der"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-semibold text-tinta">Curso</span>
              <select
                data-campo="curso-sugestao"
                className={CAMPO}
                value={registrando.curso}
                onChange={(e) => setAto({ ...registrando, curso: e.target.value })}
              >
                <option value="">Sem curso (sugestão de curso novo)</option>
                {lista.cursos.map((c) => (
                  <option key={c.slug} value={c.slug}>
                    {c.nome}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-semibold text-tinta">Categoria</span>
              <select
                data-campo="categoria-sugestao"
                className={CAMPO}
                value={registrando.categoria}
                onChange={(e) => setAto({ ...registrando, categoria: e.target.value })}
              >
                {categoriasAtivas.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.rotulo}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-semibold text-tinta">Prioridade</span>
              <select
                data-campo="prioridade-sugestao"
                className={CAMPO}
                value={registrando.prioridade}
                onChange={(e) => setAto({ ...registrando, prioridade: e.target.value as Prioridade })}
              >
                {PRIORIDADES.map((p) => (
                  <option key={p} value={p}>
                    {ROTULO_DA_PRIORIDADE[p]}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-semibold text-tinta">Por onde chegou</span>
              <select
                data-campo="origem-sugestao"
                className={CAMPO}
                value={registrando.origem}
                onChange={(e) => setAto({ ...registrando, origem: e.target.value as Origem })}
              >
                {ORIGENS.map((o) => (
                  <option key={o} value={o}>
                    {ROTULO_DA_ORIGEM[o]}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {falha?.onde === "registrar" && <Recado tipo="erro" texto={falha.texto} />}
          <div className="mt-5 flex flex-wrap gap-3">
            {/* Sem `disabled` por campo vazio: quem recusa é o banco, com a frase
                que diz o que falta. */}
            <button
              type="button"
              data-registrar-sugestao
              disabled={salvando}
              onClick={() => void registrar()}
              className="pill-cta min-h-12 px-6 disabled:opacity-55"
            >
              {salvando ? "Registrando…" : "Registrar"}
            </button>
            <button
              type="button"
              onClick={() => {
                setAto(null);
                setFalha(null);
              }}
              className="btn-contorno min-h-12 text-sm"
            >
              Cancelar
            </button>
          </div>
        </section>
      )}

      {recado && <Recado tipo={recado.tipo} texto={recado.texto} />}

      {/* ── Os filtros ─────────────────────────────────────────────────────── */}
      <div className="surface rise p-5">
        {/* Os chips contam sobre os OUTROS filtros (o banco faz a conta): sem
            isso, escolher um status mudaria o número dos outros chips e eles
            mentiriam — a cicatriz dos combos do balcão. */}
        <div className="flex flex-wrap gap-2" role="group" aria-label="Status">
          <button
            type="button"
            data-status-chip="todas"
            aria-pressed={status === ""}
            onClick={() => setStatus("")}
            className={`min-h-10 rounded-full border px-4 text-sm font-semibold ${
              status === "" ? "border-acento bg-acento text-acento-tinta" : "border-borda text-tinta"
            }`}
          >
            Todas · {totalGeral}
          </button>
          {STATUS.map((s) => (
            <button
              key={s}
              type="button"
              data-status-chip={s}
              aria-pressed={status === s}
              onClick={() => setStatus(status === s ? "" : s)}
              className={`min-h-10 rounded-full border px-4 text-sm font-semibold ${
                status === s ? "border-acento bg-acento text-acento-tinta" : "border-borda text-tinta"
              }`}
            >
              {ROTULO_DO_STATUS[s]} · {lista.contagem[s] ?? 0}
            </button>
          ))}
        </div>
        {/* Os quatro recortes (000321 trouxe a prioridade e a origem) numa grade,
            e a busca embaixo, na largura toda. Cada um entra no recorte da
            contagem dos chips — o banco faz a conta. */}
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <label className="flex min-w-0 flex-col gap-1.5">
            <span className="text-sm font-semibold text-tinta">Curso</span>
            <select data-filtro="curso" className={CAMPO} value={curso} onChange={(e) => setCurso(e.target.value)}>
              <option value="">Todos os cursos</option>
              <option value="-">Sem curso</option>
              {lista.cursos.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.nome}
                </option>
              ))}
            </select>
          </label>
          <label className="flex min-w-0 flex-col gap-1.5">
            <span className="text-sm font-semibold text-tinta">Categoria</span>
            <select
              data-filtro="categoria"
              className={CAMPO}
              value={categoria}
              onChange={(e) => setCategoria(e.target.value)}
            >
              <option value="">Todas as categorias</option>
              {lista.categorias.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.rotulo}
                  {c.ativo ? "" : " (desligada)"}
                </option>
              ))}
            </select>
          </label>
          <label className="flex min-w-0 flex-col gap-1.5">
            <span className="text-sm font-semibold text-tinta">Prioridade</span>
            <select
              data-filtro="prioridade"
              className={CAMPO}
              value={prioridade}
              onChange={(e) => setPrioridade(e.target.value as Prioridade | "")}
            >
              <option value="">Todas as prioridades</option>
              {PRIORIDADES.map((p) => (
                <option key={p} value={p}>
                  {ROTULO_DA_PRIORIDADE[p]}
                </option>
              ))}
            </select>
          </label>
          <label className="flex min-w-0 flex-col gap-1.5">
            <span className="text-sm font-semibold text-tinta">Por onde chegou</span>
            <select
              data-filtro="origem"
              className={CAMPO}
              value={origem}
              onChange={(e) => setOrigem(e.target.value as Origem | "")}
            >
              <option value="">Todas as origens</option>
              {ORIGENS.map((o) => (
                <option key={o} value={o}>
                  {ROTULO_DA_ORIGEM[o]}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="mt-4">
          <CampoDeBusca
            valor={busca}
            aoMudar={setBusca}
            aoBuscar={() => setBuscaAplicada(busca)}
            rotulo="Buscar sugestão"
            placeholder="Título, descrição ou número"
            aoLimpar={
              busca || buscaAplicada
                ? () => {
                    setBusca("");
                    setBuscaAplicada("");
                  }
                : undefined
            }
          />
        </div>
      </div>

      {/* ── A lista ─────────────────────────────────────────────────────────── */}
      <section className="surface rise overflow-hidden" role="group" aria-labelledby="titulo-lista-sugestoes">
        <div className="border-b border-borda p-5">
          <h3 id="titulo-lista-sugestoes" className="font-display text-base font-bold text-titulo">
            {lista.total} sugestão(ões)
          </h3>
          <p className="mt-1 text-sm text-cinza">
            As novas primeiro, e as de prioridade alta no alto de cada grupo.
          </p>
        </div>
        {lista.itens.length === 0 ? (
          <p className="p-6 text-sm text-cinza" data-sugestoes-vazio>
            {totalGeral === 0 && !buscaAplicada && !curso && !categoria && !prioridade && !origem
              ? "Nenhuma sugestão registrada ainda."
              : "Nenhuma sugestão com esses filtros."}
          </p>
        ) : (
          <ul className="divide-y divide-borda" data-lista-sugestoes={lista.total}>
            {lista.itens.map((s) => (
              <LinhaDaSugestao
                key={s.id}
                s={s}
                categorias={lista.categorias}
                cursos={lista.cursos}
                ato={ato && ato.tipo !== "registrar" && ato.id === s.id ? ato : null}
                aoAbrir={(a) => {
                  setFalha(null);
                  setRecado(null);
                  setAto(a);
                }}
                aoMudar={(a) => setAto(a)}
                aoFechar={() => {
                  setAto(null);
                  setFalha(null);
                }}
                aoTratar={() => void tratar()}
                aoSalvarEdicao={() => void salvarEdicao()}
                aoExcluir={() => void excluir()}
                salvando={salvando}
                podeExcluir={podeExcluir}
                falha={falha && falha.onde.endsWith(`:${s.id}`) ? falha.texto : null}
              />
            ))}
          </ul>
        )}
        {lista.itens.length < lista.total && (
          <div className="border-t border-borda p-4">
            <button type="button" onClick={() => setLimite(limite + 50)} className="btn-contorno min-h-10 text-sm">
              Mostrar mais ({lista.total - lista.itens.length} restantes)
            </button>
          </div>
        )}
      </section>

      {lista.excluidas_total > 0 && <Excluidas excluidas={lista.excluidas} total={lista.excluidas_total} />}

      <Categorias categorias={lista.categorias} aoMudar={recarregar} />
    </div>
  );
}

/**
 * O REGISTRO das exclusões (000319) — para ninguém precisar perguntar para onde
 * foi a nº X. Fechado por padrão: é consulta, não o trabalho do dia. Todo mundo
 * que vê a aba o lê; só o master exclui.
 */
function Excluidas({ excluidas, total }: { excluidas: SugestaoExcluida[]; total: number }) {
  return (
    <details className="surface rounded-cf-lg p-5" data-excluidas-sugestao={total}>
      <summary className="cursor-pointer text-sm font-semibold text-tinta">
        Excluídas ({total}) — o registro fica
      </summary>
      <p className="mt-2 text-sm text-cinza">
        Só um master exclui. A sugestão sai da lista com o histórico, e a fotografia dela fica
        guardada, com o motivo e quem excluiu.
        {total > excluidas.length ? ` Aqui aparecem as ${excluidas.length} mais recentes.` : ""}
      </p>
      <ul className="mt-4 divide-y divide-borda rounded-cf border border-borda">
        {excluidas.map((e) => (
          <li key={e.id} className="p-3" data-excluida={e.numero}>
            <p className="text-sm font-semibold text-tinta">
              <span className="mr-2 text-cinza">nº {e.numero}</span>
              {e.titulo}
            </p>
            <p className="mt-1 text-xs text-cinza">
              excluída em {dataCurta(e.em)} por {e.por}
              {e.clickup_id ? " · tinha vindo do ClickUp" : ""}
            </p>
            <p className="mt-1 text-sm text-tinta">
              <span className="font-semibold">Motivo:</span> {e.motivo}
            </p>
          </li>
        ))}
      </ul>
    </details>
  );
}

function LinhaDaSugestao({
  s,
  categorias,
  cursos,
  ato,
  aoAbrir,
  aoMudar,
  aoFechar,
  aoTratar,
  aoSalvarEdicao,
  aoExcluir,
  salvando,
  podeExcluir,
  falha,
}: {
  s: Sugestao;
  categorias: CategoriaDeSugestao[];
  cursos: { slug: string; nome: string }[];
  ato: Exclude<Ato, { tipo: "registrar" }> | null;
  aoAbrir: (a: Ato) => void;
  aoMudar: (a: Ato) => void;
  aoFechar: () => void;
  aoTratar: () => void;
  aoSalvarEdicao: () => void;
  aoExcluir: () => void;
  salvando: boolean;
  podeExcluir: boolean;
  falha: string | null;
}) {
  const [historico, setHistorico] = useState<EventoDaSugestao[] | null>(null);
  const aberta = ato !== null;

  useEffect(() => {
    if (!aberta) return;
    let vivo = true;
    void (async () => {
      const r = await lerHistoricoDaSugestao(s.id);
      if (vivo) setHistorico(r.ok ? (r.eventos ?? []) : []);
    })();
    return () => {
      vivo = false;
    };
  }, [aberta, s.id, s.atualizada_em]);

  return (
    <li className="p-5" data-sugestao={s.numero} data-origem={s.origem}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-tinta">
            <span className="mr-2 text-cinza">nº {s.numero}</span>
            {s.titulo}
          </p>
          {/* A prioridade saiu daqui em 28/09: ela está no selo, ao lado do
              status, e dizer duas vezes na mesma linha é ruído. */}
          <p className="mt-1 text-xs text-cinza" data-detalhes-sugestao>
            {s.curso_nome ?? "sem curso"} · {s.categoria} · {ROTULO_DA_ORIGEM[s.origem]} ·{" "}
            {s.registrada_por ?? "—"}, {dataCurta(s.registrada_em)}
            {/* A importada diz de onde veio: o original, com os prints e a
                identificação do aluno que o hub não guarda, está lá. */}
            {s.clickup_url && (
              <>
                {" · "}
                <a
                  href={s.clickup_url}
                  target="_blank"
                  rel="noreferrer"
                  data-clickup={s.clickup_id ?? ""}
                  className="font-semibold text-link underline"
                >
                  veio do ClickUp<span className="sr-only"> (abre em outra aba)</span>
                </a>
              </>
            )}
          </p>
          {s.retorno && (
            <p className="mt-2 text-sm text-tinta" data-retorno>
              <span className="font-semibold">Retorno:</span> {s.retorno}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2" data-selos-sugestao>
          <span
            data-prioridade={s.prioridade}
            className={`rounded-full px-2.5 py-1 text-xs ${CLASSE_DA_PRIORIDADE[s.prioridade]}`}
          >
            Prioridade {ROTULO_DA_PRIORIDADE[s.prioridade].toLowerCase()}
          </span>
          <span data-status={s.status} className={`rounded-full px-2.5 py-1 text-xs font-semibold ${CLASSE_DO_STATUS[s.status]}`}>
            {ROTULO_DO_STATUS[s.status]}
          </span>
        </div>
      </div>

      {!aberta && (
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            data-tratar={s.numero}
            onClick={() => aoAbrir({ tipo: "tratar", id: s.id, status: s.status, retorno: "", motivo: "" })}
            className="btn-contorno min-h-9 px-3 text-xs"
          >
            Tratar
          </button>
          <button
            type="button"
            data-editar-sugestao={s.numero}
            onClick={() =>
              aoAbrir({
                tipo: "editar",
                id: s.id,
                titulo: s.titulo,
                descricao: s.descricao ?? "",
                curso: s.curso_slug ?? "",
                categoria: s.categoria_id,
                prioridade: s.prioridade,
                origem: s.origem,
                motivo: "",
              })
            }
            className="btn-contorno min-h-9 px-3 text-xs"
          >
            Editar
          </button>
          {/* Por último e com a cor de erro: é o único desta linha sem volta
              pela tela. E só para o master — ver o cabeçalho. */}
          {podeExcluir && (
            <button
              type="button"
              data-excluir-sugestao={s.numero}
              onClick={() => aoAbrir({ tipo: "excluir", id: s.id, motivo: "" })}
              className="min-h-9 rounded-full border border-erro-borda px-3 text-xs font-semibold text-erro-tinta transition hover:bg-erro-fundo"
            >
              Excluir
            </button>
          )}
        </div>
      )}

      {aberta && (
        <div className="mt-4 rounded-cf border border-borda p-4">
          {s.descricao && <p className="whitespace-pre-line text-sm text-tinta">{s.descricao}</p>}

          {ato?.tipo === "tratar" && (
            <div className={s.descricao ? "mt-4" : ""}>
              <div className="grid gap-4 sm:grid-cols-3">
                <label className="flex flex-col gap-1.5">
                  <span className="text-sm font-semibold text-tinta">Status</span>
                  <select
                    data-campo="status-sugestao"
                    className={CAMPO}
                    value={ato.status}
                    onChange={(e) => aoMudar({ ...ato, status: e.target.value as StatusDaSugestao })}
                  >
                    {STATUS.map((k) => (
                      <option key={k} value={k}>
                        {ROTULO_DO_STATUS[k]}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1.5 sm:col-span-2">
                  <span className="text-sm font-semibold text-tinta">Retorno ao CS</span>
                  <textarea
                    data-campo="retorno-sugestao"
                    rows={2}
                    className={`${CAMPO} min-h-16 resize-y py-2`}
                    value={ato.retorno}
                    onChange={(e) => aoMudar({ ...ato, retorno: e.target.value })}
                    placeholder={s.retorno ? "Em branco mantém o retorno de antes" : "O que o CS vai dizer ao aluno"}
                  />
                </label>
                <label className="flex flex-col gap-1.5 sm:col-span-3">
                  <span className="text-sm font-semibold text-tinta">Por quê (quando reabrir)</span>
                  <input
                    data-campo="motivo-sugestao"
                    className={CAMPO}
                    value={ato.motivo}
                    onChange={(e) => aoMudar({ ...ato, motivo: e.target.value })}
                    placeholder="Só é pedido para tirar a sugestão de um desfecho"
                  />
                </label>
              </div>
              {falha && <Recado tipo="erro" texto={falha} />}
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  data-salvar-status
                  disabled={salvando}
                  onClick={aoTratar}
                  className="pill-cta min-h-10 px-5 text-sm disabled:opacity-55"
                >
                  {salvando ? "Salvando…" : "Salvar"}
                </button>
                <button type="button" onClick={aoFechar} className="btn-contorno min-h-10 text-sm">
                  Fechar
                </button>
              </div>
            </div>
          )}

          {ato?.tipo === "editar" && (
            <div className={s.descricao ? "mt-4" : ""}>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="flex flex-col gap-1.5 sm:col-span-2">
                  <span className="text-sm font-semibold text-tinta">Título</span>
                  <input
                    data-campo="titulo-edicao"
                    className={CAMPO}
                    maxLength={200}
                    value={ato.titulo}
                    onChange={(e) => aoMudar({ ...ato, titulo: e.target.value })}
                  />
                </label>
                <label className="flex flex-col gap-1.5 sm:col-span-2">
                  <span className="text-sm font-semibold text-tinta">Descrição</span>
                  <textarea
                    data-campo="descricao-edicao"
                    rows={3}
                    className={`${CAMPO} min-h-20 resize-y py-2`}
                    value={ato.descricao}
                    onChange={(e) => aoMudar({ ...ato, descricao: e.target.value })}
                  />
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="text-sm font-semibold text-tinta">Curso</span>
                  <select className={CAMPO} data-campo="curso-edicao" value={ato.curso} onChange={(e) => aoMudar({ ...ato, curso: e.target.value })}>
                    <option value="">Sem curso</option>
                    {cursos.map((c) => (
                      <option key={c.slug} value={c.slug}>
                        {c.nome}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="text-sm font-semibold text-tinta">Categoria</span>
                  <select
                    className={CAMPO}
                    data-campo="categoria-edicao"
                    value={ato.categoria}
                    onChange={(e) => aoMudar({ ...ato, categoria: e.target.value })}
                  >
                    {categorias
                      .filter((c) => c.ativo || c.id === s.categoria_id)
                      .map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.rotulo}
                        </option>
                      ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="text-sm font-semibold text-tinta">Prioridade</span>
                  <select
                    className={CAMPO}
                    value={ato.prioridade}
                    onChange={(e) => aoMudar({ ...ato, prioridade: e.target.value as Prioridade })}
                  >
                    {PRIORIDADES.map((p) => (
                      <option key={p} value={p}>
                        {ROTULO_DA_PRIORIDADE[p]}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="text-sm font-semibold text-tinta">Por onde chegou</span>
                  <select className={CAMPO} value={ato.origem} onChange={(e) => aoMudar({ ...ato, origem: e.target.value as Origem })}>
                    {ORIGENS.map((o) => (
                      <option key={o} value={o}>
                        {ROTULO_DA_ORIGEM[o]}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1.5 sm:col-span-2">
                  <span className="text-sm font-semibold text-tinta">Justifique a alteração</span>
                  <input
                    className={CAMPO}
                    value={ato.motivo}
                    onChange={(e) => aoMudar({ ...ato, motivo: e.target.value })}
                    placeholder="Vai para o histórico da sugestão"
                  />
                </label>
              </div>
              {falha && <Recado tipo="erro" texto={falha} />}
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  data-salvar-edicao
                  disabled={salvando}
                  onClick={aoSalvarEdicao}
                  className="pill-cta min-h-10 px-5 text-sm disabled:opacity-55"
                >
                  {salvando ? "Salvando…" : "Salvar"}
                </button>
                <button type="button" onClick={aoFechar} className="btn-contorno min-h-10 text-sm">
                  Fechar
                </button>
              </div>
            </div>
          )}

          {ato?.tipo === "excluir" && (
            <div className={s.descricao ? "mt-4" : ""} data-excluindo-sugestao={s.numero}>
              {/* O que acontece é dito ANTES do clique, e o que não tem volta
                  também: a tela não oferece desfazer. */}
              <div className="rounded-cf border border-erro-borda bg-erro-fundo p-4 text-sm text-erro-tinta">
                <p className="font-semibold">Excluir a sugestão nº {s.numero}?</p>
                <p className="mt-1">
                  Ela e o histórico dela saem da lista, sem volta pela tela. O registro das
                  exclusões, no fim da página, guarda a fotografia dela, o motivo e quem excluiu.
                </p>
              </div>
              <label className="mt-4 flex flex-col gap-1.5">
                <span className="text-sm font-semibold text-tinta">Por que ela sai? (obrigatório)</span>
                <input
                  data-campo="motivo-exclusao-sugestao"
                  className={CAMPO}
                  value={ato.motivo}
                  onChange={(e) => aoMudar({ ...ato, motivo: e.target.value })}
                  placeholder="Ex.: registrada em dobro, ou era um teste"
                />
              </label>
              {falha && <Recado tipo="erro" texto={falha} />}
              <div className="mt-4 flex flex-wrap gap-2">
                {/* Sem `disabled` por motivo vazio: quem decide que ele é
                    obrigatório é o banco, e a recusa diz o que falta. */}
                <button
                  type="button"
                  data-confirmar-exclusao-sugestao
                  disabled={salvando}
                  onClick={aoExcluir}
                  className="min-h-10 rounded-full bg-erro-tinta px-5 text-sm font-bold text-superficie disabled:opacity-60"
                >
                  {salvando ? "Excluindo…" : "Excluir a sugestão"}
                </button>
                <button type="button" onClick={aoFechar} className="btn-contorno min-h-10 text-sm">
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {/* O HISTÓRICO da sugestão: quem fez cada coisa. É o que responde
              "quem disse ao CS que não dava?" sem ninguém precisar lembrar. */}
          <div className="mt-5 border-t border-borda pt-4">
            <h4 className="text-sm font-semibold text-tinta">Histórico</h4>
            {historico === null ? (
              <p className="mt-2 text-xs text-cinza">Carregando…</p>
            ) : (
              <ol className="mt-2 space-y-1.5 text-xs text-cinza" data-historico-sugestao={historico.length}>
                {historico.map((h) => (
                  <li key={h.id}>
                    <span className="text-tinta">{new Date(h.em).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}</span>
                    {" · "}
                    {h.acao === "registrou" && "registrou"}
                    {h.acao === "mudou_status" &&
                      `${h.de_status ? ROTULO_DO_STATUS[h.de_status] : "—"} → ${h.para_status ? ROTULO_DO_STATUS[h.para_status] : "—"}`}
                    {h.acao === "editou" && `editou (${((h.detalhe?.campos as string[] | undefined) ?? []).join(", ")})`}
                    {h.acao === "importou" && textoDaImportacao(h.detalhe as ProcedenciaDoClickUp | null)}
                    {" · "}
                    {h.por}
                    {h.motivo ? ` — ${h.motivo}` : ""}
                    {h.acao === "importou" && (
                      <TriagemNoClickUp procedencia={h.detalhe as ProcedenciaDoClickUp | null} />
                    )}
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      )}
    </li>
  );
}

/**
 * A frase do evento `importou` (000319). O status de LÁ vai junto porque a
 * triagem nova recomeça do zero ("nova", a pedido de Victor), e saber que o
 * ClickUp já a tinha recusado ou concluído muda o que se faz com ela.
 */
function textoDaImportacao(d: ProcedenciaDoClickUp | null): string {
  const partes = [
    d?.status ? `lá estava «${d.status}»` : null,
    d?.prints ? `${plural(d.prints, "print ficou", "prints ficaram")} no ClickUp` : null,
    d?.trechos_retirados
      ? `${plural(d.trechos_retirados, "trecho com dado do aluno foi retirado", "trechos com dado do aluno foram retirados")}`
      : null,
  ].filter(Boolean);
  return `importada do ClickUp como nova${partes.length ? ` (${partes.join(" · ")})` : ""}`;
}

/**
 * A conversa da triagem ANTIGA — os comentários e as respostas em fio que a
 * equipe trocou no ClickUp. Veio junto porque é o que a triagem nova precisa
 * ler, e fica FECHADA: é contexto, não o trabalho do dia. Áudio e imagem só
 * existem lá, e o texto diz isso no lugar deles.
 */
function TriagemNoClickUp({ procedencia }: { procedencia: ProcedenciaDoClickUp | null }) {
  const conversa = procedencia?.conversa ?? [];
  const nota = procedencia?.descricao_da_tarefa ?? null;
  const total = conversa.reduce((soma, c) => soma + 1 + (c.respostas?.length ?? 0), 0);
  if (total === 0 && !nota) return null;
  return (
    <details className="mt-1.5 rounded-cf border border-borda p-3" data-conversa-clickup={total}>
      <summary className="cursor-pointer text-xs font-semibold text-tinta">
        A triagem no ClickUp ({plural(total, "mensagem", "mensagens")})
      </summary>
      {nota && (
        <p className="mt-2 whitespace-pre-line text-xs text-tinta">
          <span className="font-semibold">Na descrição da tarefa:</span> {nota}
        </p>
      )}
      <ol className="mt-2 space-y-2">
        {conversa.map((c, i) => (
          <li key={i}>
            <MensagemDoClickUp m={c} />
            {c.respostas && c.respostas.length > 0 && (
              <ol className="mt-1.5 ml-3 space-y-1.5 border-l border-borda pl-3">
                {c.respostas.map((r, j) => (
                  <li key={j}>
                    <MensagemDoClickUp m={r} />
                  </li>
                ))}
              </ol>
            )}
          </li>
        ))}
      </ol>
    </details>
  );
}

function MensagemDoClickUp({ m }: { m: ComentarioDoClickUp }) {
  return (
    <>
      <p className="text-xs text-cinza">
        <span className="font-semibold text-tinta">{m.autor}</span>
        {m.em ? ` · ${dataEHora(m.em)}` : ""}
      </p>
      <p className="whitespace-pre-line text-xs text-tinta">{m.texto}</p>
    </>
  );
}

/**
 * As CATEGORIAS — de Produtos. Fechadas por padrão: é a configuração, não o
 * trabalho do dia. Salvar pede editar no Catálogo, e a recusa diz isso.
 * Não há apagar: cada sugestão guarda a categoria, e categoria que não serve
 * mais DESLIGA — sai da escolha e continua na história.
 *
 * Não há mais «pede professor»: o espelho na Central foi desligado em 30/09
 * (000331) e retirado (000333), e a função das categorias não tem mais o
 * parâmetro.
 */
function Categorias({ categorias, aoMudar }: { categorias: CategoriaDeSugestao[]; aoMudar: () => Promise<void> }) {
  const [nova, setNova] = useState({ rotulo: "" });
  const [nomes, setNomes] = useState<Record<string, string>>({});
  const [recado, setRecado] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);

  async function salvar(campos: Parameters<typeof salvarCategoriaDeSugestao>[0]) {
    setRecado(null);
    const r = await salvarCategoriaDeSugestao(campos);
    setRecado({ tipo: r.ok ? "ok" : "erro", texto: mensagemDaSugestao(r) });
    if (r.ok) {
      setNomes({});
      await aoMudar();
    }
    return r;
  }

  return (
    <details className="surface rounded-cf-lg p-5" data-categorias-sugestao>
      <summary className="cursor-pointer text-sm font-semibold text-tinta">
        Categorias ({categorias.filter((c) => c.ativo).length} ativas) — mantidas por Produtos
      </summary>
      <p className="mt-2 text-sm text-cinza">
        Categoria que não serve mais se desliga: ela sai da escolha e continua nas sugestões
        antigas.
      </p>
      <ul className="mt-4 divide-y divide-borda rounded-cf border border-borda">
        {categorias.map((c) => {
          const texto = nomes[c.id] ?? c.rotulo;
          const mudou = texto.trim() !== c.rotulo;
          return (
            <li key={c.id} className="flex flex-wrap items-center gap-3 p-3" data-categoria={c.id}>
              <input
                className={`${CAMPO} min-w-56 flex-1 ${c.ativo ? "" : "text-cinza line-through"}`}
                value={texto}
                aria-label={`Nome da categoria ${c.rotulo}`}
                onChange={(e) => setNomes({ ...nomes, [c.id]: e.target.value })}
              />
              {mudou && (
                <button type="button" onClick={() => void salvar({ id: c.id, rotulo: texto })} className="btn-contorno min-h-10 text-xs">
                  Salvar nome
                </button>
              )}
              <button
                type="button"
                onClick={() => void salvar({ id: c.id, ativo: !c.ativo })}
                className="btn-contorno min-h-10 text-xs"
              >
                {c.ativo ? "Desligar" : "Religar"}
              </button>
              <span className="text-xs text-cinza">{c.sugestoes} sugestão(ões)</span>
            </li>
          );
        })}
      </ul>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <input
          className={`${CAMPO} min-w-56 flex-1`}
          data-campo="nova-categoria"
          value={nova.rotulo}
          onChange={(e) => setNova({ ...nova, rotulo: e.target.value })}
          placeholder="Nova categoria"
        />
        <button
          type="button"
          data-criar-categoria
          onClick={async () => {
            const r = await salvar({ rotulo: nova.rotulo });
            if (r.ok) setNova({ rotulo: "" });
          }}
          className="btn-contorno min-h-10 text-sm"
        >
          Criar categoria
        </button>
      </div>
      {recado && <Recado tipo={recado.tipo} texto={recado.texto} />}
    </details>
  );
}
